<template>
  <div class="sd-search-pagi">
    <div class="sd-pagi-wrapper">
      <pagination
        :isNext="isNext"
        :isPrev="isPrev"
        :showNext="isJiraNext || isConfluenceNext"
        :showPrev="currentPage > 1"
        :next="next"
        :previous="previous"
        :loader="loader"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import mixin from '../mixins/Mixins';
import store from '../../store/Store';
import Pagination from '../../components/Pagination';

export default {
  name: 'sd-search-pagination',
  mixins: [mixin],
  components: {
    Pagination,
  },
  store,
  props: [
    'routeKey',
    'jiraTotal',
    'confluenceNextReq',
    'confluencePreviousReq',
    'textSearch',
    'maxResults'
  ],

  computed: {
    currentPage() {
      return this.$store.state.currentPage;
    },
    confluencePageIncrement() {
      return this.$store.state.confluencePageIncrement;
    },
    jiraIncrementPage() {
      return this.$store.state.jiraIncrementPage;
    },
    transition() {
      return this.$store.state.transition;
    },
    loader() {
      return this.$store.state.loader;
    },
    startAt() {
      return this.$store.state.startAt;
    },
    userName() {
      return this.$store.state.user.userName;
    },
    searchConfluence() {
      return this.$store.state.filters.searchConfluence;
    },
    searchJira() {
      return this.$store.state.filters.searchJira;
    },
    searchPersonal() {
      return this.$store.state.filters.searchPersonal;
    },
    searchStatus() {
      return this.$store.state.filters.searchStatus;
    },
    sortKey() {
      return this.$store.state.filters.sortKey;
    },
    sortOrder() {
      return this.$store.state.filters.sortOrder;
    },
    searchClosed() {
      return this.$store.state.filters.searchClosed;
    },
    searchOpened() {
      return this.$store.state.filters.searchOpened;
    },
    isJiraNext() {
      if ((this.startAt + this.maxResults) < this.jiraTotal) {
        return true;
      }
      return false;
    }
  },

  data() {
    return {
      isNext: false,
      isPrev: false,
      isConfluenceNext: ((typeof this.confluenceNextReq !== 'undefined') &&
        (this.confluenceNextReq.length > 0)) || false,
      confluenceNextRequest: this.confluenceNextReq,
      confluencePreviousRequest: this.confluencePreviousReq,
    };
  },

  methods: {
    // Increment the page index for Jira or Confluence
    increment(currentPage, variable, method) {
      if (currentPage > variable) {
        this.$store.commit(method, variable + 1);
      }
    },

    previous() {
      if (!this.loader) {
        this.scrollTop();
        this.isPrev = true;
        this.paginationAction(true);
      }
    },

    next() {
      if (!this.loader) {
        this.scrollTop();
        this.isNext = true;
        this.paginationAction(false);
      }
    },

    // Request Confluence
    getConfluence(request) {
      return axios.get(request, {
        headers: {
          Pragma: 'no-cache',
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/json',
        },
      }).then((response) => {
        const promise = { response, promise: 'confluence' };
        return promise;
      }).catch((error) => {
        this.logError(error);
      });
    },

    // Request Jira
    getJira() {
      const portal = `portal=${this.routeKey}`;
      const searchJira = this.jqlQueryGenerator(
        this.textSearch,
        portal,
        this.startAt,
        this.maxResults,
        this.searchPersonal,
        this.searchJira,
        this.searchStatus,
        this.searchOpened,
        this.searchClosed,
        this.sortKey,
        this.sortOrder,
      );
      return axios.get(searchJira, {
        headers: {
          Pragma: 'no-cache',
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/json',
        },
      }).then((response) => {
        const promise = { response, promise: 'jira' };
        return promise;
      }).catch((error) => {
        this.logError(error);
      });
    },

    // Handle promises responses
    handleResponses(promises) {
      promises.forEach((p) => {
        if (typeof p !== 'undefined' && 'promise' in p) {
          if (p.promise === 'jira') {
            this.$store.commit('SET_START_AT', p.response.data.startAt);
            this.$store.commit('SET_JIRA_RESULTS', p.response.data.issues);
          } else if (p.promise === 'confluence') {
            this.$store.commit('SET_START_AT', p.response.data.start);
            this.$store.commit('SET_CONFLUENCE_RESULTS', p.response.data.results);
            const portal = `portal=${this.routeKey}`;

            this.confluenceNextRequest = p.response.data._links.next ?
              this.cqlQueryGenerator(
                this.textSearch,
                portal,
                p.response.data.start + 5,
              ) : undefined;
            this.isConfluenceNext = (this.confluenceNextRequest) || false;
            // if 'no _links.prev' use actual request because it's the first one
            this.confluencePreviousRequest = this.cqlQueryGenerator(
              this.textSearch,
              portal,
              p.response.data.start - 5,
            );
          }
        }
      });
    },

    paginationAction(isPrev) {
      const promises = [];
      // Unset Confluence and Jira comment openedIndex
      this.$store.commit('SET_COMMENT_OPENED_INDEX', -1);
      this.$store.commit('SET_CONFLUENCE_OPENED_INDEX', -1);
      this.$store.commit('SET_LOADER', true);

      if (isPrev) {
        const startPoint = (this.jiraIncrementPage === this.currentPage - 1)
          ? this.startAt
          : (this.startAt - this.maxResults);

        this.$store.commit('SET_START_AT', startPoint);
        this.$store.commit('SET_TRANSITION', 'list-right');
        if (this.searchConfluence) {
          if ((this.currentPage - 1) <= this.confluencePageIncrement) {
            promises.push(this.getConfluence(this.confluencePreviousRequest));
          }
        }

        // Previous Jira page query
        if (this.searchJira || this.searchPersonal) {
          if ((this.currentPage - 1) <= this.jiraIncrementPage) {
            promises.push(this.getJira());
          }
        }
      } else {
        this.$store.commit('SET_TRANSITION', 'list-left');
        if (this.searchConfluence && this.isConfluenceNext) {
          this.increment(
            this.currentPage + 1,
            this.confluencePageIncrement,
            'SET_CONFLUENCE_PAGE_INCREMENT',
          );
          promises.push(this.getConfluence(this.confluenceNextRequest));
        }

        // Next Jira page query
        if ((this.searchJira || this.searchPersonal) && this.isJiraNext) {
          this.increment(
            this.currentPage + 1,
            this.jiraIncrementPage,
            'SET_JIRA_PAGE_INCREMENT',
          );
          this.$store.commit('SET_START_AT', this.startAt + this.maxResults);
          promises.push(this.getJira());
        }
      }
      Promise.all(promises).then((response) => {
        this.$store.commit(
          'SET_CURRENT_PAGE',
          !isPrev ? this.currentPage + 1 : this.currentPage - 1,
        );
        this.isPrev = false;
        this.isNext = false;
        this.$store.commit('SET_LOADER', false);

        this.handleResponses(response);
      });
    },

    checkIsJiraNext(jiraResponse) {
      if ((parseInt(jiraResponse.startAt, 10) +
        parseInt(jiraResponse.maxResults, 10)) <
        parseInt(jiraResponse.total, 10)) {
        return true;
      }
      return false;
    },
  },
};
</script>

<style lang="scss">
@import "./../../Globals.scss";

.sd-search-pagi {
  overflow: hidden;
  max-width: $sd-max-width;
  margin: auto;

  .sd-pagi-wrapper {
    padding-top: 45px;
  }
}
</style>


// WEBPACK FOOTER //
// SearchPagination.vue?5728b051